import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Check } from "@phosphor-icons/react";

export function ContentPickerModal({
  open, onOpenChange, historyItems, historyLoading, selectedItems,
  onToggleItem, onAddItems, pickerFilter, onPickerFilterChange,
  pickerTypeFilter, onPickerTypeFilterChange, pickerStatusFilter,
  onPickerStatusFilterChange,
}) {
  const contentTypes = [...new Set(historyItems.map((i) => i.content_type).filter(Boolean))];

  const filteredHistory = historyItems.filter((item) => {
    if (pickerFilter && !(item.topic || "").toLowerCase().includes(pickerFilter.toLowerCase()) && !(item.title || "").toLowerCase().includes(pickerFilter.toLowerCase())) return false;
    if (pickerTypeFilter && item.content_type !== pickerTypeFilter) return false;
    if (pickerStatusFilter && item.status !== pickerStatusFilter) return false;
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--bg-raised)] border-[var(--border-strong)] text-[var(--text-primary)] max-w-2xl max-h-[80vh]" data-testid="content-picker-modal">
        <DialogHeader>
          <DialogTitle className="text-[var(--text-primary)]">Add Content Items</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <div className="flex-1 relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={pickerFilter} onChange={(e) => onPickerFilterChange(e.target.value)} placeholder="Search topics..." className="pl-8 bg-[var(--bg-base)] border-[var(--border-strong)] h-10 sm:h-8 text-xs" data-testid="picker-search" />
          </div>
          <div className="flex gap-2">
            <Select value={pickerTypeFilter} onValueChange={onPickerTypeFilterChange}>
              <SelectTrigger className="flex-1 sm:w-[140px] h-10 sm:h-8 text-xs bg-[var(--bg-base)] border-[var(--border-strong)]"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Types</SelectItem>
              {contentTypes.map((ct) => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={pickerStatusFilter} onValueChange={onPickerStatusFilterChange}>
            <SelectTrigger className="flex-1 sm:w-[120px] h-10 sm:h-8 text-xs bg-[var(--bg-base)] border-[var(--border-strong)]"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="">All Status</SelectItem>
              {["draft", "in_review", "approved", "published"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[400px] space-y-1">
          {historyLoading ? (
            <div className="text-center py-8 text-[var(--text-muted)] text-sm">Loading history...</div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-muted)] text-sm">No content items found</div>
          ) : filteredHistory.map((item) => {
            const isSelected = selectedItems.some((i) => i.history_id === item.history_id);
            return (
              <div
                key={item.history_id}
                onClick={() => onToggleItem(item)}
                className={`flex items-center gap-3 px-3 py-3 rounded-md cursor-pointer transition-all ${
                  isSelected ? "bg-[#FF4400]/10 border border-[#FF4400]/20" : "hover:bg-[var(--bg-hover)] border border-transparent"
                }`}
                data-testid={`picker-item-${item.history_id}`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                  isSelected ? "border-[#FF4400] bg-[#FF4400]" : "border-[var(--border-strong)]"
                }`}>
                  {isSelected && <Check size={12} className="text-white" weight="bold" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--text-primary)] truncate">{item.topic || item.title || "Untitled"}</p>
                  <p className="text-[10px] text-[var(--text-muted)]">{item.content_type} | {item.platform} | {item.status || "draft"}</p>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[var(--text-secondary)]">Cancel</Button>
          <Button onClick={onAddItems} disabled={selectedItems.length === 0} className="bg-[#FF4400] hover:bg-[#FF6633] text-white" data-testid="picker-add-btn">
            Add {selectedItems.length} Item{selectedItems.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
